// =============================================================================
// features/configuracoes/comercial/OutcomesSection.tsx — Grupo "Desfechos"
// (doc 26 §6.9). Salva de forma INDEPENDENTE das demais seções — é um
// recurso separado na API (`PUT /api/sales/outcomes`, replace completo).
//
// Prova de que "desfecho desabilitado some das telas de registro" está em
// __tests__/OutcomesSection.test.tsx — o mesmo endpoint `GET /api/sales/outcomes`
// consumido aqui é o que as telas de registro do vendedor usam para montar o
// seletor de desfecho (backend já filtra `enabled=false` fora, testado em
// apps/api/src/modules/sales/settings/__tests__/service.test.ts).
// =============================================================================

import type { SalesOutcomeConfig } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';

import { OutcomeRow } from './OutcomeRow';
import { SettingsCard } from './SettingsCard';

function itemsEqual(a: SalesOutcomeConfig[], b: SalesOutcomeConfig[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    return (
      other !== undefined &&
      item.code === other.code &&
      item.enabled === other.enabled &&
      item.label === other.label &&
      item.requiresNote === other.requiresNote &&
      item.requiresAppointment === other.requiresAppointment &&
      item.blocksContact === other.blocksContact
    );
  });
}

interface OutcomesSectionProps {
  items: SalesOutcomeConfig[];
  isDefault: boolean;
  isPending: boolean;
  /** Chamado com o payload completo já pronto para `PUT /api/sales/outcomes`. */
  onSave: (next: SalesOutcomeConfig[], changedCount: number) => void;
}

export function OutcomesSection({
  items,
  isDefault,
  isPending,
  onSave,
}: OutcomesSectionProps): React.JSX.Element {
  const [draft, setDraft] = React.useState<SalesOutcomeConfig[]>(items);
  const lastSyncedItems = React.useRef(items);

  // Ressincroniza o rascunho quando a lista do servidor muda de identidade
  // (ex: após `PUT /api/sales/outcomes` bem-sucedido) — só sobrescreve
  // edições locais que ainda NÃO foram salvas (draft === última versão
  // sincronizada); nunca descarta uma edição pendente sob os pés do usuário.
  // Isto é sincronização de buffer local com uma prop já carregada, não um
  // fetch disparado por efeito.
  React.useEffect(() => {
    if (items !== lastSyncedItems.current) {
      setDraft((prevDraft) => (itemsEqual(prevDraft, lastSyncedItems.current) ? items : prevDraft));
      lastSyncedItems.current = items;
    }
  }, [items]);

  const dirty = !itemsEqual(draft, items);

  const updateItem = (next: SalesOutcomeConfig): void => {
    setDraft((prev) => prev.map((item) => (item.code === next.code ? next : item)));
  };

  const disabledCount = draft.filter((item) => !item.enabled).length;

  const handleSave = (): void => {
    const changed = draft.filter((item, i) => {
      const original = items[i];
      return (
        original === undefined ||
        original.enabled !== item.enabled ||
        original.label !== item.label ||
        original.requiresNote !== item.requiresNote ||
        original.requiresAppointment !== item.requiresAppointment ||
        original.blocksContact !== item.blocksContact
      );
    });
    onSave(draft, changed.length);
  };

  const handleReset = (): void => setDraft(items);

  return (
    <SettingsCard
      title="Desfechos"
      description="Habilite ou desabilite desfechos do catálogo, ajuste o rótulo e o que cada um exige ao ser registrado."
      isDefault={isDefault}
    >
      {disabledCount > 0 && (
        <Badge variant="neutral">
          {disabledCount} desfecho{disabledCount !== 1 ? 's' : ''} desabilitado
          {disabledCount !== 1 ? 's' : ''} — não aparece{disabledCount !== 1 ? 'm' : ''} nas telas
          de registro
        </Badge>
      )}

      <div className="overflow-x-auto rounded-sm" style={{ border: '1px solid var(--border)' }}>
        <table className="w-full border-collapse" aria-label="Desfechos comerciais">
          <thead>
            <tr style={{ background: 'var(--bg-elev-2)' }}>
              {['Ativo', 'Código', 'Rótulo', 'Observação', 'Retorno', 'Bloqueio'].map((col) => (
                <th
                  key={col}
                  className="px-4 py-2.5 text-left font-sans font-bold uppercase text-ink-3"
                  style={{
                    fontSize: '0.65rem',
                    letterSpacing: '0.08em',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draft.map((item) => (
              <OutcomeRow key={item.code} item={item} onChange={updateItem} disabled={isPending} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="ghost" disabled={!dirty || isPending} onClick={handleReset}>
          Descartar
        </Button>
        <Button type="button" variant="primary" disabled={!dirty || isPending} onClick={handleSave}>
          {isPending ? 'Salvando…' : 'Salvar desfechos'}
        </Button>
      </div>
    </SettingsCard>
  );
}
